// configDisplay — the Configurations view's shared context + display grammar.
// List · peek · form all read the SAME catalogue context, the SAME absence
// grammar (charterMeta), and the SAME renderers, so band/designation names,
// completeness, gap severities, and status can't drift between surfaces.
//
// The absence grammar's three volumes, one look each (his ruling):
//   structural absence = SILENCE (never rendered, never counted) ·
//   applicable absence = grey quiet "—" ·
//   severity lives at the AGGREGATE by ROLE — priced gap = the loud
//   Incomplete state (service-refused), commitment gap = warning-toned
//   fraction (breach checks blind).

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  configIncompleteFields,
  effectiveCharter,
  type CharterField,
  type CharterNature,
  type ConfigValuesView,
} from '@ems-app/schemas/invoicing/charter';
import type { BandFamily } from '@ems-app/schemas/invoicing/charter';
import type { ContractualConfig } from '@ems-app/schemas/invoicing/responses';
import { byImpact, familyDef, type ObservationImpact, type ObservationKind } from '@ems-app/schemas/observations/registry';
import { IMPACT_TEXT, ObservationMark, ObservationMarkRow, kindMarkSummaries } from '../../components/patterns/observations.js';
import { Badge } from '../../components/ui/Badge.js';
import { icons, type Icon } from '../../components/ui/icons.js';
import { formatBandRange, formatValue } from '../../lib/formatValue.js';
import { useCharter, useBandFamily, type Band } from '../../hooks/useContractCatalogue.js';
import { useSiteTypes } from '../../hooks/useSiteTypes.js';
import { useSitePriorities } from '../../hooks/useSitePriorities.js';
import { useSiteCategories } from '../../hooks/useSiteCategories.js';
import {
  CORE_FIELDS,
  ENV_FIELDS,
  FAMILY_CHARTER_FIELD,
  FAMILY_COLS,
  FAMILY_DIMENSION,
  SOURCES,
  VALUE_FIELD_DIM,
  applicableValueFields,
  committedFamilies,
  pricedSources,
  type Natures,
  type SourceMeta,
  type ValueFieldMeta,
} from './charterMeta.js';

/** Minimal translate signature the pure renderers take (list cells build their
 *  rows outside component scope, so `t` travels as a value). */
export type Tr = (key: string, opts?: Record<string, unknown>) => string;

// ── the catalogue context (one load, all consumers) ──────────────────────────
export interface CatalogueContext {
  natures: Natures;
  bands: Record<BandFamily, Band[]>;
  siteTypes: Map<number, string>;
  priorities: Map<number, string>;
  categories: Map<number, string>;
  isPending: boolean;
  isError: boolean;
  /** Refetch the failed context queries (the consumer's retry affordance). */
  refetch: () => void;
}

export function useCatalogueContext(projectId: string): CatalogueContext {
  const charter = useCharter(projectId);
  const load = useBandFamily(projectId, 'load');
  const grid = useBandFamily(projectId, 'grid_availability');
  const solar = useBandFamily(projectId, 'solar');
  const cooling = useBandFamily(projectId, 'cooling');
  const types = useSiteTypes(projectId);
  const priorities = useSitePriorities(projectId);
  const categories = useSiteCategories(projectId);

  const queries = [charter, load, grid, solar, cooling, types, priorities, categories];
  const isPending = queries.some((q) => q.isPending);
  const isError = queries.some((q) => q.isError);

  const natures = useMemo(() => effectiveCharter(charter.data?.fields ?? []), [charter.data]);
  const bands = useMemo(
    () => ({
      load: load.data?.items ?? [],
      grid_availability: grid.data?.items ?? [],
      solar: solar.data?.items ?? [],
      cooling: cooling.data?.items ?? [],
    }),
    [load.data, grid.data, solar.data, cooling.data],
  );
  const nameMap = (items: { id: number; name: string }[] | undefined) => new Map((items ?? []).map((x) => [x.id, x.name]));
  const siteTypes = useMemo(() => nameMap(types.data), [types.data]);
  const prioMap = useMemo(() => nameMap(priorities.data), [priorities.data]);
  const catMap = useMemo(() => nameMap(categories.data), [categories.data]);

  const refetch = () => {
    for (const q of queries) if (q.isError) void q.refetch();
  };

  return { natures, bands, siteTypes, priorities: prioMap, categories: catMap, isPending, isError, refetch };
}

// ── same-pricing detection (the address-uniqueness health signal) ────────────
// A config occupies a CELL in the price grid; several configs can sit in ONE
// cell — legal variants (they differ on reference/commitment values) OR an
// accidental collision (removing a priced axis collapses configs that differed
// only on it). Either way the funnel returns them ALL as candidates for a site
// there → no clean batch-accept. So we flag configs that SHARE a priced address.
// Derived, never stored (ADR-061); EXACT match on the PRICED axes only (never
// commitments/reference values — those legitimately distinguish variants);
// partial designation-set overlap is left to the matching funnel's real
// per-site candidate sets (a later phase). Only ENABLED + COMPLETE configs
// participate (a disabled config is no candidate; an incomplete one has no full
// address — already flagged).
// The canonical priced-address key from a value view (a full config OR a live
// form draft — both expose the ConfigValuesView shape). No gating here; callers
// gate on disabled/complete.
function addressKeyFromView(v: ConfigValuesView, natures: Natures): string {
  const r = v as unknown as Record<string, unknown>;
  const parts: string[] = [`ld:${v.load_band_id}`];
  if (natures.environment === 'priced') parts.push(`env:${v.environment ?? ''}`);
  if (natures.site_type === 'priced') parts.push(`ty:${[...v.site_type_ids].sort((a, b) => a - b).join(',')}`);
  if (natures.priority === 'priced') parts.push(`pr:${[...v.priority_ids].sort((a, b) => a - b).join(',')}`);
  if (natures.category === 'priced') parts.push(`ca:${[...v.category_ids].sort((a, b) => a - b).join(',')}`);
  for (const s of SOURCES) {
    if (natures[s.requiredField] === 'priced') parts.push(`${s.key}:${r[s.requiredField]}`);
    if (s.family && s.bandField && natures[s.bandField] === 'priced') {
      const cols = FAMILY_COLS[s.family];
      const none = cols.none ? r[cols.none] === true : false;
      parts.push(`${s.key}b:${none ? 'none' : ((r[cols.id] as number | null) ?? '')}`);
    }
  }
  return parts.join('|');
}

function pricedAddressKey(config: ContractualConfig, natures: Natures): string | null {
  if (config.is_disabled || configIncompleteFields(config, natures).length > 0) return null;
  return addressKeyFromView(config as unknown as ConfigValuesView, natures);
}

/** Per-config → the OTHER configs sharing its exact priced address (empty when
 *  unique / disabled / incomplete). One pass over the loaded set (tens of
 *  rows). The chip, the filter, the group lens, and the peek all read this. */
export function samePricingPeers(configs: ContractualConfig[], natures: Natures): Map<number, ContractualConfig[]> {
  const byKey = new Map<string, ContractualConfig[]>();
  for (const c of configs) {
    const k = pricedAddressKey(c, natures);
    if (k === null) continue;
    const bucket = byKey.get(k);
    if (bucket) bucket.push(c);
    else byKey.set(k, [c]);
  }
  const out = new Map<number, ContractualConfig[]>();
  for (const group of byKey.values()) {
    if (group.length < 2) continue;
    for (const c of group) out.set(c.id, group.filter((o) => o.id !== c.id));
  }
  return out;
}

/** The configs (enabled + complete, excluding `excludeId`) sharing a live
 *  draft view's exact priced address — the config form's birth-warning ("you're
 *  landing on B2-S2's address"). Empty until the draft is complete. */
export function collidingConfigs(
  view: ConfigValuesView,
  viewComplete: boolean,
  natures: Natures,
  others: ContractualConfig[],
  excludeId?: number,
): ContractualConfig[] {
  if (!viewComplete) return [];
  const key = addressKeyFromView(view, natures);
  return others.filter((c) => c.id !== excludeId && pricedAddressKey(c, natures) === key);
}

/** The group-by-"Same pricing" coordinate: shared configs group by their exact
 *  priced address; everything else folds into one 'unique' bucket. */
export function samePricingGroupKey(config: ContractualConfig, natures: Natures, peers: Map<number, ContractualConfig[]>): string {
  return peers.has(config.id) ? (pricedAddressKey(config, natures) ?? 'unique') : 'unique';
}

/** A compact readable address for a shared cluster's group header: the present
 *  priced sources + load + priced bands (enough to tell clusters apart). */
export function pricedAddressLabel(ctx: CatalogueContext, natures: Natures, config: ContractualConfig, t: Tr): string {
  const present = SOURCES.filter(
    (s) => natures[s.requiredField] === 'priced' && config[s.requiredField as keyof ContractualConfig] === true,
  ).map((s) => t(`form.sources.${s.key}`));
  const parts = [present.length > 0 ? present.join(' + ') : t('configs.glyph.noSources'), bandLabel(ctx, 'load', config.load_band_id)];
  for (const f of ['grid_availability', 'solar', 'cooling'] as const) {
    const src = SOURCES.find((s) => s.family === f)!;
    if (natures[src.bandField!] !== 'priced') continue;
    const cols = FAMILY_COLS[f];
    if (cols.none && config[cols.none] === true) continue;
    const id = config[cols.id] as number | null;
    if (id != null) parts.push(bandLabel(ctx, f, id));
  }
  return parts.join(' · ');
}

// ── band labels ──────────────────────────────────────────────────────────────
/** "S2 [10 – 15 kW]" — the locked band format: NAME [range]; id unknown → em dash. */
export function bandLabel(ctx: CatalogueContext, family: BandFamily, id: number | null): string {
  if (id == null) return '—';
  const band = ctx.bands[family].find((b) => b.id === id);
  if (!band) return '—';
  const range = formatBandRange(band.min, band.max, FAMILY_DIMENSION[family]);
  return range ? `${band.name} [${range}]` : band.name;
}

// ── the one source lens (raw keys — labels + order resolved by the page) ─────
// The special buckets carry the contract's words, never a raw dash: an axis
// distinguishes the MISSING answer (the question is unanswered), the absence
// coordinate ("Not required" — a real pricing address), PRESENT ("Required",
// when the tiers are simply not used), and the bands. One bucket order
// everywhere — Missing · not required · required · the value ladder — the
// same scale sort-ascending walks; filters and groups read the same keys.
export const GROUP_NONE = 'none';
export const GROUP_MISSING = 'missing';
export const GROUP_PRESENT = 'present';

/** A band family's group coordinate: 'missing' | 'none' | the band NAME —
 *  or 'present' when the family's band axis is NOT engaged (the tiers are
 *  simply not used), so the lens degrades to missing · no · yes. Missing
 *  covers both an unanswered band pair AND an unanswered presence (the pair
 *  can't be read while the source question is open). */
export function bandGroupKey(natures: Natures, ctx: CatalogueContext, family: BandFamily, c: ContractualConfig): string {
  const cols = FAMILY_COLS[family];
  const src = SOURCES.find((s) => s.family === family);
  const on = src ? (c[src.requiredField as keyof ContractualConfig] as boolean | null) : true;
  const isNone = cols.none ? c[cols.none] === true : false;
  if (on === false || isNone) return GROUP_NONE;
  if (on == null) return GROUP_MISSING;
  const bandField = src?.bandField;
  const bandEngaged = bandField ? natures[bandField] !== 'off' : natures.load_band !== 'off';
  if (!bandEngaged) return GROUP_PRESENT;
  const id = c[cols.id] as number | null;
  if (id == null) return GROUP_MISSING;
  return ctx.bands[family].find((b) => b.id === id)?.name ?? GROUP_MISSING;
}

/** The COMPOSITION group coordinate — the derived archetype (the sites
 *  solution-type pattern: the derived value is the GROUP lens, its
 *  constituents stay the filter). Reads the PRICED-address source set (the
 *  glyph's set — commitment-only sources live in the commitments rollup):
 *  present sources joined '+' in registry order · 'none' for a no-source
 *  cell · 'missing' while any address presence is unanswered (an undeclared
 *  composition classifies nowhere). */
export function compositionGroupKey(natures: Natures, c: ContractualConfig): string {
  const address = pricedSources(natures);
  if (address.some((s) => c[s.requiredField as keyof ContractualConfig] == null)) return GROUP_MISSING;
  const present = address.filter((s) => c[s.requiredField as keyof ContractualConfig] === true);
  if (present.length === 0) return GROUP_NONE;
  return present.map((s) => s.key).join('+');
}

// ── source vocabulary (the composition grammar's words) ──────────────────────
export const SOURCE_ICON: Record<SourceMeta['key'], Icon> = {
  grid: icons.sourceGrid,
  solar: icons.sourceSolar,
  generator: icons.sourceGenerator,
  cooling: icons.sourceCooling,
  wind: icons.sourceWind,
};

/** A source's ABSENCE in the contract's own word — "Not required", uniform
 *  across every source (the axes are binary: required / not required; the
 *  `*_required` columns ARE the grammar). */
export function sourceAbsenceLabel(t: Tr): string {
  return t('form.presence.notRequired');
}

/** The bandless tri-state lens key for a source: 'missing' | 'none' |
 *  'present' — the same coordinate space bandGroupKey degrades to. */
export function presenceKey(c: ContractualConfig, src: SourceMeta): string {
  const on = c[src.requiredField as keyof ContractualConfig] as boolean | null;
  return on == null ? GROUP_MISSING : on === false ? GROUP_NONE : GROUP_PRESENT;
}

/** ONE lens per source axis (filters · groups · anything that browses by a
 *  source): bands when the family's band axis is engaged, the binary
 *  tri-state otherwise. */
export function sourceLensKey(natures: Natures, ctx: CatalogueContext, src: SourceMeta, c: ContractualConfig): string {
  if (src.family && src.bandField && natures[src.bandField] !== 'off') return bandGroupKey(natures, ctx, src.family, c);
  return presenceKey(c, src);
}

/** A present source's word — the CONTRACT speaks: "Required", generator
 *  enriched with its committed unit count ("2 generators required"). */
export function presenceLabel(t: Tr, s: SourceMeta, config: ContractualConfig): string {
  if (s.key === 'generator') {
    const units = config.generator_units_min;
    if (units != null && units > 0) return t('form.presence.generatorsRequired', { count: units });
  }
  return t('form.presence.required');
}

/** A value field's display text ('—' for an applicable gap). */
export function valueText(config: ContractualConfig, meta: ValueFieldMeta, t: Tr): string {
  const raw = config[meta.key];
  if (raw == null) return '—';
  if (meta.dim === 'text') return t(`form.batteryChemistry.${String(raw)}`);
  const f = formatValue(raw as number, meta.dim);
  return f ? f.display : '—';
}

/** Fold a name list: "A, B" or "A +2"; empty → em dash. */
export function foldNames(names: string[]): string {
  if (names.length === 0) return '—';
  if (names.length <= 2) return names.join(', ');
  return `${names[0]} +${names.length - 1}`;
}

// ── the pricing row builder (the peek's Pricing-drivers section) ─────────────
// Label = the SOURCE; a banded source answers band [range] / its absence word /
// Missing; a bandless one answers Required / its absence word / Missing.
// Presence and band are INDEPENDENT terms — when both are priced they COLLAPSE
// to one row (the band carries the answer; its axis holds the none
// coordinate). Pricing rows are the ADDRESS: absence words are real
// coordinates, so an absent source keeps its row.
export interface SourceRow {
  label: string;
  value: string;
  missing: boolean;
}

export function pricingSourceRows(
  ctx: CatalogueContext,
  natures: Natures,
  config: ContractualConfig,
  t: Tr,
): SourceRow[] {
  const rows: SourceRow[] = [];
  for (const s of SOURCES) {
    const presenceNature = natures[s.requiredField];
    const bandNature: CharterNature = s.bandField ? natures[s.bandField] : 'off';
    const on = config[s.requiredField as keyof ContractualConfig];
    const label = t(`form.sources.${s.key}`);
    const cols = s.family ? FAMILY_COLS[s.family] : null;
    const isNone = cols?.none ? config[cols.none] === true : false;
    const id = cols ? (config[cols.id] as number | null) : null;

    const bandAnswer = (): SourceRow => {
      let value: string;
      let missing = false;
      if (on === false || isNone) value = sourceAbsenceLabel(t);
      else if (on == null) {
        missing = presenceNature === 'priced' || bandNature === 'priced';
        value = missing ? t('configs.missing') : '—';
      } else if (id != null) value = bandLabel(ctx, s.family!, id);
      else if (bandNature === 'priced') {
        missing = true;
        value = t('configs.missing');
      } else value = '—';
      return { label, value, missing };
    };

    if (presenceNature === 'priced') {
      if (bandNature === 'priced') {
        // Both priced → the band row alone (the collapse rule); a present
        // source whose band answer is silent reads its presence word.
        const row = bandAnswer();
        rows.push(row.value === '—' && on === true ? { ...row, value: presenceLabel(t, s, config) } : row);
      } else {
        const missing = on == null;
        rows.push({
          label,
          value: on === true ? presenceLabel(t, s, config) : on === false ? sourceAbsenceLabel(t) : t('configs.missing'),
          missing,
        });
      }
    } else if (bandNature === 'priced') {
      // A priced band under a non-priced presence keeps its address row.
      rows.push(bandAnswer());
    }
  }
  return rows;
}

// ── the clustered commitments (ONE builder — the peek section, the rollup
// panel, and the fraction all read it, so the surfaces can't drift) ─────────
// The Terms page's cluster grammar applied to a CONFIG's answers: the source
// named once (its icon; the presence answer rides the heading), SHORT term
// labels beneath (terms.short.*), the core kit first, Carbon & renewables
// last. Structural silence holds per cluster: an absent source is a
// heading-only cluster (its value terms are inapplicable); a source with
// nothing committed never renders. A committed band (the contract-bound size
// window — ADR-060 §2) rows only while its source is present.
export interface CommitmentSection {
  key: string;
  icon: Icon;
  name: string;
  /** The presence answer when the presence itself is commitment-nature —
   *  rides the cluster heading ('—' = an applicable gap, quiet Missing). */
  presence?: string;
  rows: SourceRow[];
}

export function commitmentSections(
  ctx: CatalogueContext,
  natures: Natures,
  config: ContractualConfig,
  t: Tr,
): CommitmentSection[] {
  const out: CommitmentSection[] = [];
  const applicable = new Set(applicableValueFields(natures, config, 'commitment').map((f) => f.key));
  const valueRows = (fields: readonly CharterField[]): SourceRow[] =>
    fields
      .filter((f) => applicable.has(f as ValueFieldMeta['key']))
      .map((f) => ({
        label: t(`terms.short.${f}`),
        value: valueText(config, VALUE_FIELD_DIM.find((m) => m.key === f)!, t),
        missing: false,
      }));

  const core = valueRows(CORE_FIELDS);
  if (core.length > 0) out.push({ key: 'core', icon: icons.powerSystem, name: t('terms.clusters.core'), rows: core });

  for (const s of SOURCES) {
    const on = config[s.requiredField as keyof ContractualConfig] as boolean | null;
    const rows: SourceRow[] = [];
    if (s.bandField && s.family && natures[s.bandField] === 'commitment' && on === true) {
      const id = config[FAMILY_COLS[s.family].id] as number | null;
      rows.push({
        label: t('terms.short.bandCommitment'),
        value: id != null ? bandLabel(ctx, s.family, id) : '—',
        missing: false,
      });
    }
    rows.push(...valueRows(s.valueFields));
    const presence =
      natures[s.requiredField] === 'commitment'
        ? on === true
          ? t('form.presence.required')
          : on === false
            ? sourceAbsenceLabel(t)
            : '—'
        : undefined;
    if (presence !== undefined || rows.length > 0) {
      out.push({ key: s.key, icon: SOURCE_ICON[s.key], name: t(`form.sources.${s.key}`), presence, rows });
    }
  }

  const env = valueRows(ENV_FIELDS);
  if (env.length > 0) out.push({ key: 'env', icon: icons.sustainability, name: t('terms.clusters.env'), rows: env });
  return out;
}

/** The fraction the commitments rollup + the peek's section count share:
 *  a slot per row + per heading presence answer; '—' = an unfilled slot. */
export function commitmentSlots(sections: CommitmentSection[]): { filled: number; total: number } {
  let filled = 0;
  let total = 0;
  for (const s of sections) {
    total += s.rows.length + (s.presence !== undefined ? 1 : 0);
    filled += s.rows.filter((r) => r.value !== '—').length + (s.presence !== undefined && s.presence !== '—' ? 1 : 0);
  }
  return { filled, total };
}

// ── renderers ────────────────────────────────────────────────────────────────
// The gap colour (one home for every surface — list · peek · form · the panels):
// a gap is coloured by its FAMILY'S IMPACT, so "Missing" is visible in place
// and what it costs to ignore is legible at a glance. The tone names keep the
// ROLE words (pricing · commitment) because that is what a config author
// thinks in — the colours come from the observation registry.
export type GapTone = 'pricing' | 'commitment';
// The tones ARE the two contract families' impacts (ADR-061 — one scale; the
// private "pricing amber · commitment blue" law dissolved with it): a priced
// gap is HIGH (matching cannot run, the invoice blocks — danger), a commitment
// gap is MEDIUM (real work, nothing blocked — warning). Change a family's
// impact in the registry and every gap word here follows.
export const GAP_TONE_CLASS: Record<GapTone, string> = {
  pricing: IMPACT_TEXT[familyDef('contract.priced_field_blank').impact],
  commitment: IMPACT_TEXT[familyDef('contract.commitment_field_blank').impact],
};

/** The in-place gap word for the PEEK + FORM — coloured by the family's impact
 *  (GAP_TONE_CLASS): danger for a priced gap (high — blocks the invoice),
 *  warning for a commitment gap (medium — real work, nothing blocked). */
export function MissingValue({ tone = 'pricing' }: { tone?: GapTone }) {
  const { t } = useTranslation('invoicing');
  return <span className={`whitespace-nowrap ${GAP_TONE_CLASS[tone]}`}>{t('configs.missing')}</span>;
}

/** The QUIET in-place gap word for LIST cells — the alarm lives in the
 *  Observations column (the sites law: the word survives, the colour goes; a
 *  bare dash would assert absence where the truth is "unanswered"). */
export function MissingWord() {
  const { t } = useTranslation('invoicing');
  return <span className="whitespace-nowrap text-sm text-[var(--color-text-secondary)]">{t('configs.missing')}</span>;
}

export function ConfigStatusBadge({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation('invoicing');
  return disabled ? <Badge variant="neutral">{t('status.disabled')}</Badge> : <Badge variant="success">{t('status.active')}</Badge>;
}

// ── THE CONFIGS COMPARATOR (ADR-061 §4) ──────────────────────────────────────
// Which families a config raises — it lives here, in the module that owns the
// data, and nothing outside the contract surface calls it. This page raises
// exactly THREE (the architect's read: any observation means the config is
// either conflicting or unusable):
//   contract.priced_field_blank      — a priced axis unanswered (blocks the save-complete state)
//   contract.commitment_field_blank  — a commitment tier/presence/value unrecorded
//   contract.same_pricing_collision  — two configs landing in one priced cell
// A DISABLED config raises nothing: it is out of service by decision, and its
// lens is the strip's Disabled chip.
export interface ConfigObservation {
  family: 'contract.priced_field_blank' | 'contract.commitment_field_blank' | 'contract.same_pricing_collision';
  kind: ObservationKind;
  impact: ObservationImpact;
  /** i18n key of WHAT exactly (a charter field / a source presence). Absent on
   *  the collision — its subject is the shared address, which the group lens
   *  and the peek's own sections already print. */
  subjectKey?: string;
}

function observe(family: ConfigObservation['family'], subjectKey?: string): ConfigObservation {
  const def = familyDef(family);
  return { family, kind: def.kind, impact: def.impact, ...(subjectKey ? { subjectKey } : {}) };
}

/** The commitment-side blanks, per field (the Data filter's old boolean, made
 *  subject-bearing): an engaged commitment band with its tier unanswered · an
 *  unanswered commitment presence · an applicable commitment value left blank. */
function commitmentGapKeys(c: ContractualConfig, natures: Natures): string[] {
  const out: string[] = [];
  for (const f of committedFamilies(natures)) {
    const src = SOURCES.find((s) => s.family === f);
    const present = src ? c[src.requiredField as keyof ContractualConfig] === true : false;
    if (present && (c[FAMILY_COLS[f].id] as number | null) == null) out.push(`charter.fields.${FAMILY_CHARTER_FIELD[f]}`);
  }
  for (const s of SOURCES) {
    if (!s.family && natures[s.requiredField] === 'commitment' && c[s.requiredField as keyof ContractualConfig] == null) {
      out.push(`form.sources.${s.key}`);
    }
  }
  for (const f of applicableValueFields(natures, c, 'commitment')) {
    if (c[f.key] == null) out.push(`charter.fields.${f.key}`);
  }
  return out;
}

export function configObservations(
  c: ContractualConfig,
  natures: Natures,
  /** The priced completeness set (the page's `missingById` / `configIncompleteFields`). */
  missingFields: readonly CharterField[],
  /** Same-pricing cluster size (≥2 = a collision). */
  clusterSize: number,
): ConfigObservation[] {
  if (c.is_disabled) return [];
  return [
    ...missingFields.map((f) => observe('contract.priced_field_blank', `charter.fields.${f}`)),
    ...commitmentGapKeys(c, natures).map((k) => observe('contract.commitment_field_blank', k)),
    ...(clusterSize >= 2 ? [observe('contract.same_pricing_collision')] : []),
  ].sort(byImpact);
}

/** THE OBSERVATIONS CELL — the configs half of the shared grammar (ADR-061 §5):
 *  one glyph per kind present (each wearing its kind's worst impact), the most
 *  impacting subject, "+N more". A clean row renders NOTHING — absence of a
 *  mark IS "fine" (the sites law). A DISABLED row wears its lifecycle badge
 *  HERE (architect-ruled): it raises no observations by construction, so the
 *  cell is guaranteed free — and the identity keeps its width. */
export function ConfigObservationsCell({ items, disabled, t }: { items: ConfigObservation[]; disabled: boolean; t: Tr }) {
  if (disabled) return <Badge variant="neutral">{t('status.disabled')}</Badge>;
  if (items.length === 0) return <span className="text-[var(--color-text-disabled)]">—</span>;
  // The T re-cut of the row face (shared with sites + assets): counted PER-KIND
  // marks, no in-cell words — subjects ride title + sr-only, the peek details.
  const marks = kindMarkSummaries(items);
  const label = marks
    .map((m) => {
      const words = items
        .filter((o) => o.kind === m.kind)
        .map((o) => (o.subjectKey ? t(o.subjectKey) : t(`observations.family.${o.family}`)));
      return `${t(`observations.kind.${m.kind}`)}: ${words.join(' · ')}`;
    })
    .join(' — ');
  return <ObservationMarkRow marks={marks} label={label} />;
}

/** The column's DEGRADED face — the bare mark, same address, tighter cell; a
 *  disabled row keeps its lifecycle signal as the compact glyph. */
export function ConfigObservationsMark({ items, disabled, t }: { items: ConfigObservation[]; disabled: boolean; t: Tr }) {
  if (disabled) {
    return (
      <span className="inline-flex text-[var(--color-text-disabled)]" title={t('status.disabled')}>
        <icons.statusArchived size={18} aria-label={t('status.disabled')} />
      </span>
    );
  }
  const top = items[0];
  if (!top) return null;
  return <ObservationMark kind={top.kind} impact={top.impact} count={items.length} bare />;
}

/** A role-section title: an OPTIONAL 16px role icon + label + the missing
 *  count, coloured by the section's role (GAP_TONE_CLASS — orange for pricing,
 *  blue for commitment), so the aggregate agrees with the in-place cells. */
export function SectionTitle({
  icon: I,
  children,
  missing,
  tone = 'pricing',
}: {
  icon?: Icon;
  children: string;
  missing?: number;
  tone?: GapTone;
}) {
  const { t } = useTranslation('invoicing');
  return (
    <span className="inline-flex items-center gap-2">
      {I && <I size={16} aria-hidden className="text-[var(--color-text-secondary)]" />}
      {children}
      {missing != null && missing > 0 && (
        <span className={`text-xs font-normal ${GAP_TONE_CLASS[tone]}`}>
          {t('configs.sections.missingCount', { count: missing })}
        </span>
      )}
    </span>
  );
}
